/**
 * CRM invoice numbers for the Business Central export (migration 982). The server allocates and validates them; these
 * helpers only mirror the rules for the settings form, show examples/ranges and clean the search box input.
 *
 * Rules (enforced by the finance_settings trigger in 982): prefix 1-10 characters of A-Z, 0-9 and "-", with at least
 * one letter (so CRM numbers never collide with Finance's own numeric series); digits a whole number 4-12; prefix plus
 * digits at most 20 characters (Business Central's document number length).
 */

export const BC_DOCUMENT_MAX = 20;

export function formatBcDocumentNo(
  prefix: string,
  pad: number,
  n: number,
): string {
  const digits = String(n);
  return prefix + (digits.length >= pad ? digits : digits.padStart(pad, "0"));
}

export function validateBcPrefix(prefix: string, pad: number): string | null {
  if (!/^[A-Z0-9-]{1,10}$/.test(prefix)) {
    return 'Use 1 to 10 capital letters, digits or "-".';
  }
  if (!/[A-Z]/.test(prefix)) {
    return "Include at least one letter so CRM numbers never match Finance's own numbers.";
  }
  if (prefix.length + pad > BC_DOCUMENT_MAX) {
    return `Prefix plus digits must fit ${BC_DOCUMENT_MAX} characters.`;
  }
  return null;
}

export function validateBcPad(pad: number, prefix: string): string | null {
  if (!Number.isInteger(pad) || pad < 4 || pad > 12) {
    return "Digits must be a whole number from 4 to 12.";
  }
  if (prefix.length + pad > BC_DOCUMENT_MAX) {
    return `Prefix plus digits must fit ${BC_DOCUMENT_MAX} characters.`;
  }
  return null;
}

/** "CRM-000001 – CRM-000004", "CRM-000007", or "—" when the batch has no CRM numbers (older template) */
export function documentRange(nos: (string | null | undefined)[]): string {
  const s = nos.filter((x): x is string => !!x).sort();
  if (s.length === 0) {
    return "—";
  }
  return s[0] === s[s.length - 1] ? s[0] : `${s[0]} – ${s[s.length - 1]}`;
}

/** search box input -> a safe ILIKE fragment (only the characters a CRM number can contain) */
export function cleanDocumentSearch(q: string): string {
  return q
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, "")
    .slice(0, BC_DOCUMENT_MAX);
}
